'use client';

import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { ProductImages } from '@eliadmin/core/src/components/product/detail/product-images';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { ProductBadge } from './product-badge';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { ProductDetailPrice } from '@eliadmin/core/src/components/product/detail/product-detail-price';
import { Plus } from 'lucide-react';
import { ProductQuantity } from '@eliadmin/core/src/components/product/detail/product-quantity';
import { ProductTags } from './product-tags';
import { ProductAskDialog } from './product-ask-dialog';
import { scrollToId } from '@eliadmin/core/src/utils/scroll';
import { ProductVariants } from '@eliadmin/core/src/components/product/detail/variants/product-variants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

export function ProductInfo({ product }: { product: ProductDetailData }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const desiredObject = product.variants?.length ? selectedVariant : product;
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, { isWholesaler });

  const descriptionToRender =
    selectedVariant?.short_description ||
    selectedVariant?.description ||
    product.short_description ||
    product.description;

  const HEADER_OFFSET = 0;

  const { t } = useTranslations();

  return (
    <section className={cn('custom-section', 'pb-12 pt-6 sm:pb-16 sm:pt-8 lg:pb-24 lg:pt-12')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between',
        )}
      >
        <div className="w-full md:max-w-[709px]">
          <ProductImages />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 sm:gap-8 md:max-w-[491px]">
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-2.5">
              <div className="flex w-full flex-wrap items-start justify-start gap-2">
                {priceDisplay.showPreviousPrice && (
                  <ProductBadge
                    text={`${t('text24')} -${priceDisplay.discountLabel}%`}
                    background="#FFD3D3"
                    color="#BE0000"
                  />
                )}
                {product.tags?.map(tag => (
                  <ProductBadge
                    key={tag.name}
                    text={tag.name}
                    background={tag.color}
                    color={getContrastYIQ(tag.color)}
                  />
                ))}
              </div>
              {product.ratingCount > 0 && (
                <ProductRating
                  rating={product.rating}
                  ratingCount={product.ratingCount}
                  className={{
                    main: 'flex items-center justify-start gap-2',
                    starIcon: 'text-[#EDB000]',
                    starDisabled: 'text-[#EBEBEB]',
                  }}
                />
              )}
              <h1 className="w-full text-2xl font-semibold text-foreground sm:text-3xl lg:text-[32px]">
                {product.name}
              </h1>
              <p className="mt-2 text-lg font-normal text-black">
                {t('text2481')}: {product.code}
              </p>
              <div className="flex w-full flex-col items-start justify-start gap-4">
                <HtmlDescription
                  color="muted-foreground"
                  content={descriptionToRender}
                  size="sm"
                  className={cn('w-full text-left', 'ea-product-info__short-description')}
                />
                <div
                  onClick={() => scrollToId('description-parameters', HEADER_OFFSET)}
                  className="flex w-full flex-wrap items-start justify-start gap-2"
                >
                  {product.description && (
                    <div className="flex h-[34px] cursor-pointer items-center justify-center gap-2 rounded-[6px] border border-border bg-white px-3 text-sm transition-all hover:bg-neutral-50">
                      <Plus size={18} className="text-foreground" />
                      {t('text452')}
                    </div>
                  )}
                  {product.attributes && product.attributes.length > 0 && (
                    <div
                      onClick={() => scrollToId('description-parameters', HEADER_OFFSET)}
                      className="flex h-[34px] cursor-pointer items-center justify-center gap-2 rounded-[6px] border border-border bg-white px-3 text-sm transition-all hover:bg-neutral-50"
                    >
                      <Plus size={18} className="text-foreground" />
                      {t('text3180')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <ProductVariants />
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <ProductTags product={product} />
            <ProductDetailPrice />
          </div>
          <ProductQuantity />
          <ProductAskDialog title={product.name} />
        </div>
      </div>
    </section>
  );
}
